import { z } from 'zod';
import {
  PANDA_PANIC_JWT_TTL_SECONDS,
  PANDA_PANIC_MAX_PLAYERS,
  PANDA_PANIC_MIN_PLAYERS,
} from './constants.js';
import { RoomCodeSchema } from './jwt.js';

/**
 * Contrat du « ticket » d'accès à une salle Panda Panic (Phase 4, lot 4c). Le
 * client demande un ticket à l'API (`POST /panda-panic/tickets`) AVANT de
 * rejoindre le serveur autoritaire :
 *  - créer → l'API GÉNÈRE le code de salle (le JWT le porte) ;
 *  - rejoindre → le client fournit le code, l'API le valide et signe le JWT.
 *
 * La réponse porte le JWT court (présenté au `join` Colyseus), le code de salle
 * et l'URL WebSocket publique du serveur autoritaire (`wss://…/game` en prod).
 * Aucune donnée cachée (ADR 0003) : que de l'identité publique et de l'adressage.
 */

/**
 * Corps de la demande de ticket. `room` absent → création (code généré par
 * l'API) ; `room` présent → rejoint cette salle (code déjà partagé hors-bande).
 * `seats` (création seule, ignoré au join) : nombre TOTAL de sièges de la
 * partie (2–4) — au lancement, les sièges sans humain sont comblés par des
 * bots côté serveur. Absent → comportement historique (pas de comblement,
 * quorum 2 humains).
 */
export const PandaPanicTicketRequestSchema = z.object({
  room: RoomCodeSchema.optional(),
  seats: z.number().int().min(PANDA_PANIC_MIN_PLAYERS).max(PANDA_PANIC_MAX_PLAYERS).optional(),
});
export type PandaPanicTicketRequest = z.infer<typeof PandaPanicTicketRequestSchema>;

/** Réponse : jeton de connexion, code de salle et point d'entrée WebSocket. */
export const PandaPanicTicketSchema = z.object({
  /** JWT court (HS256) présenté au `join` Colyseus, lié à `room` (spec §12). */
  token: z.string().min(1),
  /** Code de la salle visée (4 lettres majuscules). */
  room: RoomCodeSchema,
  /** URL WebSocket du serveur autoritaire (ex. `wss://redpan.dev/game`). */
  wsUrl: z.string().min(1),
  /** Durée de vie du jeton (s) — le client rejoint aussitôt. */
  ttlSeconds: z.number().int().positive().default(PANDA_PANIC_JWT_TTL_SECONDS),
});
export type PandaPanicTicket = z.infer<typeof PandaPanicTicketSchema>;
